import { useEffect, useState } from "react";
import {
  ApiError,
  decideVerification,
  fetchEvidence,
  listSectors,
  readTenantApplication,
  type CertificationBody,
  type Sector,
} from "../api/client";
import { TextAreaField } from "../components/TextAreaField";
import { idempotencyKey } from "../api/idempotency";

/**
 * Screens 10 and 11 — one certification body's application, and the Cl. 5.2 decision.
 *
 * They are one screen. Screen 11 is the same layout with a warning bar above it, shown when
 * the body declares management system consultancy; building two would mean two places to
 * change the day the accreditation panel gains a field.
 *
 * What is shown is the application *as submitted*, read back through the same reader the
 * body's own screen 08 uses. Opening it writes an access event naming the administrator to
 * the body's own chain, so this is not a page to wander into: it is a reading of a
 * certification body's file and the record says so.
 *
 * The duration threshold is the body's stored value. The prototype prints 75% here, and 75
 * is the number MD 5 Cl. 2.1.2 does not permit — the floor is 80.
 */
export function VerifyCertificationBody({
  tenantId,
  onDone,
  onBack,
}: {
  tenantId: string;
  onDone: () => void;
  onBack: () => void;
}) {
  const [body, setBody] = useState<CertificationBody | null>(null);
  const [sectors, setSectors] = useState<readonly Sector[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    Promise.all([readTenantApplication(tenantId), listSectors()])
      .then(([application, list]) => {
        setBody(application);
        setSectors(list);
      })
      .catch(report);
  }, [tenantId]);

  function report(cause: unknown) {
    setError(
      cause instanceof ApiError ? cause.message : "The application could not be read.",
    );
  }

  /**
   * One key per decision, minted here and reused on a retry of the same click.
   *
   * A key minted inside the request would make every retry a fresh write, which is the
   * defect the header exists to prevent.
   */
  const [key] = useState(() => `verification-${idempotencyKey()}`);

  const decide = async (decision: "verify" | "request_more_information") => {
    setError(null);
    setBusy(true);
    try {
      await decideVerification(
        tenantId,
        decision === "verify"
          ? { decision }
          : { decision, note: note.trim() },
        key,
      );
      onDone();
    } catch (cause) {
      report(cause);
    } finally {
      setBusy(false);
    }
  };

  if (body === null) {
    return (
      <>
        <BackLink onBack={onBack} />
        {error ? (
          <p className="problem" role="alert">
            {error}
          </p>
        ) : (
          <p className="sub">Opening the application&hellip;</p>
        )}
      </>
    );
  }

  const verified = body.verified_at !== null;

  return (
    <>
      <BackLink onBack={onBack} />

      <div className="between" style={{ marginBottom: "16px" }}>
        <div>
          <div className="eyebrow">Verification</div>
          <h1>{body.trading_name ?? body.legal_name}</h1>
          <p className="sub">
            {body.legal_name} · <span className="mono">{body.registration_number}</span>
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          {verified ? (
            <>
              <span className="pill p-ok">Verified</span>
              <div className="sub" style={{ marginTop: "5px" }}>
                {body.verified_by} · {formatDate(body.verified_at)}
              </div>
            </>
          ) : (
            <span className="pill p-wait">Pending</span>
          )}
        </div>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {/* Screen 11. Recorded rather than refused at onboarding, because it bears on this
          decision and not on whether the application could be made. */}
      {body.consultancy_offered ? (
        <div className="warnbar">
          <b>Declares management system consultancy.</b> Cl.&nbsp;5.2.5 requires strict
          separation. Confirm the body&rsquo;s own safeguards before activating.
        </div>
      ) : null}

      <div className="callout">
        <b>Check these three things.</b> That the accreditation number exists on{" "}
        {body.accreditation_body}&rsquo;s public register and is active; that every claimed
        sector falls inside the accredited scope on that register; and that the liability
        cover has not lapsed. Activating a body that is not accredited for what it claims
        puts Complao in the chain of a worthless certificate.
      </div>

      <div className="grid2">
        <div className="card">
          <h2>Accreditation claimed</h2>
          <dl className="kv">
            <dt>Body</dt>
            <dd>{body.accreditation_body}</dd>
            <dt>Number</dt>
            <dd>
              <span className="mono">{body.accreditation_number}</span>
            </dd>
            <dt>Status</dt>
            <dd>{body.accreditation_status}</dd>
            <dt>Expires</dt>
            <dd>{body.accreditation_expiry ?? "—"}</dd>
            <dt>Next assessment</dt>
            <dd>{body.next_assessment ?? "—"}</dd>
            <dt>IAF MLA</dt>
            <dd>{body.mla_signatory ? "Signatory" : "Not a signatory"}</dd>
            <dt>Liability</dt>
            <dd>{body.liability_cover ?? "—"}</dd>
          </dl>

          <div className="hr" />
          <div className="eyebrow">Evidence supplied</div>
          {(body.evidence ?? []).length === 0 ? (
            <p className="sub">No evidence was attached.</p>
          ) : (
            (body.evidence ?? []).map((entry) => (
              <Evidence key={entry.id} tenantId={tenantId} id={entry.id} name={entry.filename} />
            ))
          )}
        </div>

        <div className="card">
          <h2>Scope claimed</h2>
          {(body.scopes ?? []).map((scope) => (
            <div key={scope.standard} className="plan-ed" style={{ marginBottom: "10px" }}>
              <b>{scope.standard}</b>
              {(scope.sectors ?? []).map((index) => (
                <div key={index} className="sub" style={{ fontSize: "12px" }}>
                  IAF {String(index).padStart(2, "0")} ·{" "}
                  {sectors.find((sector) => sector.index === index)?.label ?? "—"}
                </div>
              ))}
            </div>
          ))}

          <div className="hr" />
          <dl className="kv">
            <dt>Impartiality cttee</dt>
            <dd>{body.impartiality_committee ? "Yes" : "No"}</dd>
            <dt>Decision maker</dt>
            <dd>{body.decision_maker ?? "—"}</dd>
            <dt>Technical manager</dt>
            <dd>{body.technical_manager ?? "—"}</dd>
            {/* The body's own figure. MD 5 Cl. 2.1.2 permits stricter than 80, never looser. */}
            <dt>Duration threshold</dt>
            <dd>{body.duration_threshold}%</dd>
          </dl>
        </div>
      </div>

      {asking ? (
        <div className="card" style={{ marginTop: "16px" }}>
          <h2>Request more information</h2>
          <p className="sub">
            This returns the application to {body.trading_name ?? body.legal_name} for
            correction and clears any verification with it. The note is what they are told,
            and it goes on their record.
          </p>
          <TextAreaField
            id="verification-note"
            label="What is missing or wrong"
            value={note}
            onChange={setNote}
            required
            rows={3}
            hint="At least 10 characters — this is the whole of what the body has to act on."
          />
          <div className="row" style={{ justifyContent: "flex-end", gap: "10px" }}>
            <button type="button" className="btn sec" onClick={() => setAsking(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || note.trim().length < 10}
              onClick={() => void decide("request_more_information")}
            >
              {busy ? "Returning…" : "Return the application"}
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ justifyContent: "flex-end", gap: "10px" }}>
          <button
            type="button"
            className="btn sec"
            disabled={busy || !body.onboarding_completed_at}
            onClick={() => setAsking(true)}
          >
            Request more information
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || verified || !body.onboarding_completed_at}
            onClick={() => void decide("verify")}
          >
            {verified ? "Verified" : busy ? "Verifying…" : "Verify and activate"}
          </button>
        </div>
      )}
    </>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" className="backlink" onClick={onBack}>
      ← Verification queue
    </button>
  );
}

/**
 * One attached file, opened on demand.
 *
 * The bytes are fetched rather than linked because the route is authenticated, and they
 * are fetched on the click rather than up front: opening evidence is a read of the body's
 * file and writes its own event, so three files must not become three events on arrival.
 */
function Evidence({
  tenantId,
  id,
  name,
}: {
  tenantId: string;
  id: string;
  name: string;
}) {
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try {
      window.open(await fetchEvidence(tenantId, id), "_blank", "noopener");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="docrow">
      <span className="mono" style={{ fontSize: "11px" }}>
        {name}
      </span>
      <button
        type="button"
        className="pill p-mute"
        style={{ marginLeft: "auto", cursor: "pointer" }}
        onClick={() => void open()}
        disabled={busy}
      >
        {busy ? "Opening…" : "Open"}
      </button>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (value === null) return "—";
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? "—"
    : when.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
